import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonToolbar,
  ButtonGroup,
  Panel,
  Stack,
  Loader,
  IconButton,
  SelectPicker
} from 'rsuite';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getBuildings, getFloorsByBuilding } from '@/services/floor.service';
import Floor from './Floor';
import BookingWindow from './BookingWindow';
import SettingsDrawer from './SettingModal';
import CalendarIcon from '@rsuite/icons/Calendar';
import SiteSettingIcon from '@rsuite/icons/SiteSetting';
import './style.css';

const ContentPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const buildings = useAppSelector((state: any) => state.building?.data || []);
  const floors = useAppSelector((state: any) => state.floor?.data || [])?.map(item => ({
    ...item,
    value: item._id,
    label: item.name
  }));
  const loadingBuildings = useAppSelector((state: any) => state.building?.loading);
  const loadingFloors = useAppSelector((state: any) => state.floor?.loading);
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [openBooking, setOpenBooking] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const handleBookingOpen = () => setOpenBooking(true);
  const handleBookingClose = () => setOpenBooking(false);
  const handleSettingsOpen = () => setOpenSettings(true);
  const handleSettingsClose = () => setOpenSettings(false);

  useEffect(() => {
    dispatch(getBuildings());
  }, [dispatch]);

  useEffect(() => {
    if (buildings?.length > 0 && !selectedBuilding && !loadingBuildings) {
      const firstBuildingId = buildings[0]?._id;
      setSelectedBuilding(firstBuildingId);
      if (firstBuildingId) {
        dispatch(getFloorsByBuilding(firstBuildingId));
      }
    }
  }, [buildings, loadingBuildings, selectedBuilding, dispatch]);

  const handleBuildingChange = (value: string | null) => {
    setSelectedBuilding(value);
    setSelectedFloor(null);
    if (value) {
      dispatch(getFloorsByBuilding(value));
    }
  };

  return (
    <Panel bodyFill style={{ background: '#fff', padding: 20 }}>
      {loadingBuildings ? (
        <Stack justifyContent="center">
          <Loader content={t('Loading offices...')} />
        </Stack>
      ) : (
        <>
          <Stack justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={10} alignItems="center">
              <SelectPicker
                data={buildings?.map(building => ({
                  value: building?._id,
                  label: building?.address
                }))}
                searchable={false}
                cleanable={true}
                style={{ width: 200, backgroundColor: 'transparent' }}
                placeholder={t('Select Office')}
                value={selectedBuilding}
                onChange={handleBuildingChange}
                loading={loadingBuildings}
              />
              {loadingFloors ? (
                <Loader content={t('Loading floors...')} />
              ) : selectedBuilding && floors?.length > 0 ? (
                <ButtonToolbar>
                  <ButtonGroup>
                    {floors.map(floor => (
                      <Button
                        key={floor.floor_num}
                        onClick={() => setSelectedFloor(floor)}
                        active={selectedFloor?.floor_num === floor.floor_num}
                      >
                        {floor.floor_num}
                      </Button>
                    ))}
                  </ButtonGroup>
                </ButtonToolbar>
              ) : (
                <p>{t('Please select an office to view floors.')}</p>
              )}
            </Stack>
            <Stack spacing={10}>
              {selectedBuilding && (
                <IconButton icon={<SiteSettingIcon />} onClick={handleSettingsOpen}>
                  {t('Settings')}
                </IconButton>
              )}
              <IconButton icon={<CalendarIcon />} onClick={handleBookingOpen}>
                {t('Booking window')}
              </IconButton>
            </Stack>
          </Stack>

          {selectedBuilding && selectedFloor ? (
            <Stack direction="column" spacing={2} style={{ marginTop: 20 }}>
              <Floor floor={selectedFloor} selectedDate={selectedDate} />
            </Stack>
          ) : (
            <Stack justifyContent="flex-start" style={{ marginTop: 50 }}>
              <p>{t('Please select a floor number.')}</p>
            </Stack>
          )}
        </>
      )}

      {openBooking && <BookingWindow open={openBooking} handleClose={handleBookingClose} />}
      {openSettings && (
        <SettingsDrawer
          open={openSettings}
          handleClose={handleSettingsClose}
          buildingId={selectedBuilding}
        />
      )}
    </Panel>
  );
};

export default ContentPage;
